import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, DollarSign, Home, Clock, Truck, MapPin, XCircle, AlertTriangle, Package
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'PENDING PAYMENT' },
  pending_fulfillment: { icon: DollarSign, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'PENDING FULFILLMENT' },
  pending_shipment: { icon: Package, color: 'text-tiffany-500', bg: 'bg-tiffany-500/10', label: 'PENDING SHIPMENT' },
  shipped: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'SHIPPED' },
  out_for_delivery: { icon: Truck, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'OUT FOR DELIVERY' },
  delivered: { icon: MapPin, color: 'text-green-500', bg: 'bg-green-500/10', label: 'DELIVERED' },
  cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'CANCELLED' },
  expired: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'EXPIRED' }
};

export default function AdminRevenue() {
  const { logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Calculate revenue stats — keep status list aligned with admin_stats.py
  // (analytics dashboard + top spenders) so all revenue totals match across
  // the site. `payment_plan` counts as confirmed because the customer has
  // committed to a deposit + plan and the order is reserved.
  const confirmedRevenue = orders
    .filter(o => ['pending_fulfillment', 'pending_shipment', 'shipped', 'out_for_delivery', 'delivered', 'payment_plan'].includes(o.status))
    .reduce((sum, o) => sum + o.total, 0);
  
  const pendingRevenue = orders
    .filter(o => o.status === 'pending')
    .reduce((sum, o) => sum + o.total, 0);
  
  const totalRevenue = orders.reduce((sum, o) => sum + o.total, 0);

  // Group orders by date
  const ordersByDate = orders
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .reduce((groups, order) => {
      const date = new Date(order.created_at).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(order);
      return groups;
    }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING REVENUE</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm">
              ← BACK TO ADMIN
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <DollarSign className="w-8 h-8 text-green-500" />
          <h1 className="font-sans text-3xl text-white tracking-tighter">
            REVENUE
          </h1>
        </div>

        {/* Revenue Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-green-500/30 bg-green-500/5 p-6 text-center">
            <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="font-mono text-3xl text-green-500">${confirmedRevenue.toFixed(2)}</p>
            <p className="font-mono text-xs text-zinc-500 mt-1">CONFIRMED REVENUE</p>
          </div>
          <div className="border border-yellow-500/30 bg-yellow-500/5 p-6 text-center">
            <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="font-mono text-3xl text-yellow-500">${pendingRevenue.toFixed(2)}</p>
            <p className="font-mono text-xs text-zinc-500 mt-1">PENDING PAYMENT</p>
          </div>
          <div className="border border-zinc-800 p-6 text-center">
            <DollarSign className="w-8 h-8 text-white mx-auto mb-2" />
            <p className="font-mono text-3xl text-white">${totalRevenue.toFixed(2)}</p>
            <p className="font-mono text-xs text-zinc-500 mt-1">TOTAL ALL ORDERS</p>
          </div>
        </div>

        {/* Orders by Date */}
        <h2 className="font-mono text-lg text-white mb-4">ORDERS BY DATE</h2>
        
        {Object.keys(ordersByDate).length === 0 ? (
          <div className="border border-zinc-800 p-8 text-center">
            <p className="font-mono text-zinc-500">NO ORDERS YET</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(ordersByDate).map(([date, dateOrders]) => {
              const dayTotal = dateOrders.reduce((sum, o) => sum + o.total, 0);
              return (
                <div key={date} className="border border-zinc-800">
                  <div className="bg-zinc-900/50 p-4 flex items-center justify-between border-b border-zinc-800">
                    <p className="font-mono text-white">{date}</p>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-zinc-500">{dateOrders.length} orders</span>
                      <span className="font-mono text-tiffany-500">${dayTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {dateOrders.map((order) => {
                      const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <div key={order.id} className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-mono text-tiffany-500 font-bold">{order.id}</p>
                            <p className="font-mono text-sm text-zinc-400">{order.user_name || order.user_email}</p>
                            <p className="font-mono text-xs text-zinc-600">
                              {new Date(order.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-lg text-white mb-1">${order.total.toFixed(2)}</p>
                            <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 rounded-none flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
